import { Children, FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { LargeSizeType } from "mds/global-types";
import { useAccordion } from "mds/hooks/use-accordion";
import { ChevronDownIcon, ChevronUpIcon } from "mds/icons";

interface AccordionPanelProps {
  className?: string;
  title: string | ReactNode;
  titleClassName?: string;
  children: ReactNode;
  startOpen?: boolean;
  titleRightContent?: ReactNode;
  emptyContent?: ReactNode;
  childrenWrapperClassName?: string;
  collapsedColor?: AccordionColorKind;
  expandedColor?: AccordionColorKind;
  expandableAnywhere?: boolean;
  iconPosition?: "start" | "end";
  iconSize?: LargeSizeType;
  hideToggle?: boolean;
}

const colorClasses = {
  default: "border border-black-tint-90 bg-white",
  blue: "border border-blue-tint-70 bg-blue-tint-95",
  green: "border border-moss-tint-55 bg-moss-tint-95",
  "light-gray": "border border-black-tint-90 bg-black-tint-99",
  "no-border-white": "border-none bg-white",
  red: "border border-red-tint-70 bg-red-tint-95",
} as const;

type AccordionColorKind = keyof typeof colorClasses;

export const AccordionPanel: FC<AccordionPanelProps> = ({
  className,
  title,
  children,
  emptyContent,
  hideToggle,
  startOpen = false,
  titleRightContent,
  titleClassName,
  childrenWrapperClassName,
  collapsedColor,
  expandedColor = "default",
  expandableAnywhere,
  iconPosition = "start",
  iconSize = "m",
}) => {
  const { expanded, toggle } = useAccordion(startOpen);
  const icon = hideToggle ? undefined : expanded ? (
    <ChevronUpIcon size={iconSize} />
  ) : (
    <ChevronDownIcon size={iconSize} />
  );

  return (
    <div
      className={twMerge(
        "accordion-panel rounded-lg p-2",
        expanded
          ? colorClasses[expandedColor ?? "default"]
          : collapsedColor
            ? colorClasses[collapsedColor]
            : "border border-black-tint-97 bg-black-tint-97",
        expandableAnywhere && "cursor-pointer",
        className,
      )}
      onClick={hideToggle ? undefined : expandableAnywhere ? toggle : undefined}
    >
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={hideToggle ? undefined : !expandableAnywhere ? toggle : undefined}
      >
        <div
          className={twMerge(
            "ml-1 flex items-center gap-2 text-sm font-medium text-black-tint-20",
            titleClassName,
          )}
        >
          {title}
          {iconPosition === "start" && icon}
        </div>
        <div className="flex items-center gap-2">
          {titleRightContent}
          {iconPosition === "end" && icon}
        </div>
      </div>

      {expanded && (
        <div className={twMerge("flex flex-col", childrenWrapperClassName)}>
          {Children.count(children) === 0 && emptyContent ? emptyContent : children}
        </div>
      )}
    </div>
  );
};
